import React, { useState, useEffect, useCallback, useRef } from 'react';
import backgroundImage from './assets/DDL_background.png';
import toonData from './data/npcs_data.json'

// Function to parse the NPC block - moved outside the component
const parseNpcBlock = (content) => {
  // Ensure content is a string before using regex
  if (typeof content !== 'string') {
    return null;
  }
  const npcBlockRegex = /\{\{NPC\s*\|([\s\S]*?)\}\}/;
  const match = content.match(npcBlockRegex);
  if (!match) return null;

  const paramsString = match[1];
  const params = {};
  paramsString.split('|').forEach(param => {
    const [key, value] = param.split('=').map(s => s.trim());
    if (key && value) {
      // Handle array values like species or affiliation by splitting if they contain ']]'
      // This is a simple heuristic and might need refinement for complex cases
      if (value.includes('[[') && value.includes(']]')) {
          params[key] = value.replace(/\[\[|\]\]/g, '').split(',').map(s => s.trim());
        // Check if the value is "N/A" and set it to null or undefined if so
      } else if (value.toLowerCase() === 'n/a') {
        params[key] = null;
      }
      else {
          params[key] = value;
      }
    }
  });
  return params;
};

// Function to convert wiki-style markdown to HTML - moved outside the component
const convertWikiMarkdownToHtml = (content) => {
  // Ensure content is a string before using regex
  if (typeof content !== 'string') {
    return '';
  }
  let html = content;

  // Remove the NPC block (including variants like NPC3, NPC2, etc.)
  html = html.replace(/\{\{NPC[^|}]*\|[\s\S]*?\}\}/g, '');

  // Remove categories
  html = html.replace(/\[\[Category:.*?\]\]/g, '');

  // Remove <ref>...</ref> and <ref .../>
  html = html.replace(/<ref[^>]*?>[\s\S]*?<\/ref>/gi, ''); // Remove <ref>...</ref>
  html = html.replace(/<ref[^>]*?\/>/gi, ''); // Remove <ref .../>

  // Remove src attributes from any HTML tags
  html = html.replace(/\s+src\s*=\s*(["']).*?\1/gi, '');

  // Remove <gallery>...</gallery> blocks and their contents
  html = html.replace(/<gallery[\s\S]*?<\/gallery>/gi, '');

  // Remove lines that are just the Gallery header (==Gallery== or ===Gallery===)
  html = html.replace(/^\s*==+\s*Gallery\s*==+\s*$(?:\r?\n|\r)?/gim, '');

  // Remove Gallery section (from ==Gallery== or ===Gallery=== to next header or end)
  // This will also remove the header itself, even if there is no content after it
  html = html.replace(/^\s*==+\s*Gallery\s*==+\s*$(?:\r?\n|\r)?([\s\S]*?)(?=^==+\s*.*?\s*==+|\Z)/gim, '');

  // Remove any remaining lines that look like 'filename.jpg|caption' (common in gallery sections)
  html = html.replace(/^.*\.(jpg|jpeg|png|gif)\|.*$/gim, '');

  // Remove References section (from ==References== or ===References=== to next header or end)
  html = html.replace(/(==+\s*References\s*==+[\s\S]*?)(?=(^==+\s*.*?\s*==+)|$)/gim, '');

  // Remove [[File:...]] and [[Image:...]] wiki links entirely
  html = html.replace(/\[\[(File|Image):[^\]]*\]\]/gi, '');

  // Convert bold: '''text''' to <strong>text</strong>
  html = html.replace(/'''(.*?)'''/g, '<strong>$1</strong>');

  // Convert internal wiki links: [[Page Name]] to plain text
  html = html.replace(/\[\[([^|\]]+?)\|([^\]]+?)\]\]/g, '$2'); // Handles [[Page Name|Display Text]]
  html = html.replace(/\[\[(.*?)\]\]/g, '$1'); // Handles [[Page Name]]

  // Convert h3 headers: ===Header=== to <h3>Header</h3> with styling
  html = html.replace(/^===\s*(.*?)\s*===$/gm, '<h3 style="font-size: 1.2rem; font-weight: bold; margin-top: 0.5rem; margin-bottom: 0.25rem; color: #fbbf24;">$1</h3>');
  // Convert headers: ==Header== to <h2>Header</h2> with styling
  html = html.replace(/^==\s*([^=][^=]*?)\s*==$/gm, '<h2 style="font-size: 1.5rem; font-weight: bold; margin-top: 0.5rem; margin-bottom: 0.25rem; color: #facc15;">$1</h2>');

  // Convert MediaWiki-style {{Quote|...}} blocks to styled blockquote
  html = html.replace(/\{\{Quote\|([^|}]*)\|?([^|}]*)\|?([^}]*)\}\}/gi, (match, quote, attribution) => {
    let result = `<blockquote style="border-left: 4px solid #facc15; margin: 0.5em 0; padding: 0.5em 1em; background: #23272e; color: #e2e8f0;">${quote.trim()}`;
    if (attribution && attribution.trim()) {
      result += `<br/><span style='font-size:0.9em;color:#aaa;'>— ${attribution.trim()}</span>`;
    }
    result += '</blockquote>';
    return result;
  });

  // Group consecutive lines starting with > into a single blockquote
  html = html.replace(/((^>.*(\n|$))+)/gm, (match) => {
    // Remove any trailing newlines
    const trimmed = match.replace(/\n+$/, '');
    // Split into lines, remove leading > and whitespace
    const lines = trimmed.split(/\n/).map(line => line.replace(/^>\s?/, '').trim());
    // Filter out empty lines
    const nonEmpty = lines.filter(line => line.length > 0);
    if (nonEmpty.length === 0) return '';
    return `<blockquote style="border-left: 4px solid #facc15; margin: 0.5em 0; padding: 0.5em 1em; background: #23272e; color: #e2e8f0;">${nonEmpty.join('<br/>')}</blockquote>`;
  });

  // Convert list items: * Item to <li>Item</li> (and wrap in ul later)
  html = html.replace(/^\*\s*(.*)$/gm, '<li>$1</li>');

  // Wrap consecutive list items in <ul> tags
  html = html.replace(/(<li>.*?<\/li>(\n<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');

  // Convert newlines to <br> for simple paragraph breaks, but avoid inside block elements
  // This regex will not add <br/> after block-level tags
  html = html.replace(/(?<!<\/(?:ul|ol|li|h2|h3|blockquote)>)\n/g, '<br/>');
  // Trim any trailing <br/> tags at the end
  html = html.replace(/(<br\/>\s*)+$/g, '');
  // Trim any leading <br/> tags at the start
  html = html.replace(/^(<br\/>\s*)+/g, '');
  return html;
};


// WikiEntryDisplay component (now in the same file for simplicity)
function WikiEntryDisplay({ markdownContent }) {
  if (typeof markdownContent !== 'string' || markdownContent.trim() === '') {
    return <p style={{color: '#cbd5e0', textAlign: 'center', fontSize: '0.8rem'}}>No wiki content available.</p>;
  }

  const npcData = parseNpcBlock(markdownContent);
  let htmlContent = convertWikiMarkdownToHtml(markdownContent);

  // Limit the displayed content to a certain length for table cells
  const maxLength = 150; // Adjust as needed
  if (htmlContent.length > maxLength) {
    htmlContent = htmlContent.substring(0, maxLength) + '...';
  }

  return (
    <div style={{ lineHeight: '1.4', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {npcData && npcData.role && <p style={{fontWeight: 'bold', marginBottom: '0.25rem'}}>Role: {npcData.role}</p>}
      {/* Display a snippet of the main wiki content */}
      <div dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
    </div>
  );
}


function App() {
  const [secretToon, setSecretToon] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [message, setMessage] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allToons, setAllToons] = useState([]); // State to store Toon data
  const [filteredToons, setFilteredToons] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state for data processing
  const [error, setError] = useState(null); // Error state for data processing
  const [gameMode, setGameMode] = useState('daily'); // 'daily' or 'unlimited'
  
  // State for the custom date picker's displayed month/year
  const [pickerMonth, setPickerMonth] = useState(new Date().getMonth());
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [completedDates, setCompletedDates] = useState({}); // Stores { 'YYYY-MM-DD': true } for completed games
  const [showDatePicker, setShowDatePicker] = useState(false); // State to toggle date picker visibility
  const [startedDates, setStartedDates] = useState({}); // Stores { 'YYYY-MM-DD': true } for started but not finished games
  const [revealOpen, setRevealOpen] = useState(true);
  const [hoveredImageUrl, setHoveredImageUrl] = useState(null);
  const [hoveredImagePos, setHoveredImagePos] = useState({ x: 0, y: 0 });

  // Function to get today's date in YYYY-MM-DD format (local timezone)
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize selectedDate with today's local date by constructing it from components
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  });

  const MAX_DAILY_GUESSES = 10;
  const inputRef = useRef(null); // Ref for the input element
  const dateInputRef = useRef(null); // Ref for the date input element - kept for potential future use or if styling needs it

  // Lucide React icons (simulated for direct embedding)
  const CheckCircle = ({ style }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );

  const XCircle = ({ style }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );

  const MinusCircle = ({ style }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
    </svg>
  );

  // Embedded Toon data (limited to first 10 items for preview)
  const TOON_DATA_EMBEDDED = toonData;

  // Effect to load Toon data from the embedded constant
  useEffect(() => {
    try {
      const data = TOON_DATA_EMBEDDED;
      const formattedToons = Object.values(data).map(toon => ({
        ...toon,
        // Ensure color and affiliation are arrays, even if single value or missing
        color: Array.isArray(toon.color) ? toon.color : (toon.color ? [toon.color] : []),
        affiliation: Array.isArray(toon.affiliation) ? toon.affiliation : (toon.affiliation ? [toon.affiliation] : []),
        image: toon.image_url || `https://placehold.co/100x100/333333/FFFFFF?text=${toon.name.charAt(0)}`
      }));
      setAllToons(formattedToons);
      setIsLoading(false);
    } catch (e) {
      console.error("Failed to process embedded Toon data:", e);
      setError("Failed to load Toon data. Please try again later.");
      setIsLoading(false);
    }
  }, []); // Run once on component mount

  // Add versioning for localStorage
  const LOCALSTORAGE_VERSION = '1.0.5';
  const LOCALSTORAGE_VERSION_KEY = 'toondle_localstorage_version';

  // On app load, check version and clear old data if needed
  useEffect(() => {
    const storedVersion = localStorage.getItem(LOCALSTORAGE_VERSION_KEY);
    if (storedVersion !== LOCALSTORAGE_VERSION) {
      // Remove all toondle-related keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('toondle_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem(LOCALSTORAGE_VERSION_KEY, LOCALSTORAGE_VERSION);
    }
  }, []);

  // Deterministic PRNG (Mulberry32)
  function seededRandom(seed) {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }

  // Function to get a deterministic daily toon index with weighted randomness
  const getDailyToonIndex = useCallback((date, eligibleToons) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    // Unique seed for each day (with offset for different values)
    const seed = year * 10000 + month * 100 + day + 69;
    const rand = seededRandom(seed);
    // Use sqrt of wiki_content length as weight
    const weights = eligibleToons.map(toon => {
      const len = toon.wiki_content && typeof toon.wiki_content === 'string' ? toon.wiki_content.length : 0;
      return Math.max(1, Math.sqrt(len));
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    let threshold = rand * totalWeight;
    for (let i = 0; i < weights.length; i++) {
      if (threshold < weights[i]) return i;
      threshold -= weights[i];
    }
    return 0;
  }, []);

  // Function to initialize or reset the game
  const initializeGame = useCallback((forceNewGame = false) => {
    if (allToons.length === 0) return; // Don't initialize if data isn't loaded yet

    let selectedToon;
    if (gameMode === 'daily') {
      // Filter toons that meet the daily eligibility criteria
      const dailyEligibleToons = allToons.filter(toon => {
        const wiki = toon.wiki_content && typeof toon.wiki_content === 'string' ? toon.wiki_content.toLowerCase() : '';
        const name = toon.name ? toon.name.toLowerCase() : '';
        return (
          (
           wiki.includes('sos') ||
           wiki.includes('sellbot task force') ||
           wiki.includes('bank') ||
           wiki.includes('speedway')) ||
          name.includes('clothing shop') ||
          wiki.length > 1000
        );
      });

      if (dailyEligibleToons.length === 0) {
        setError("No eligible Toons found for daily game. Please check data.");
        setIsLoading(false); // Stop loading if no valid toons
        return;
      }

      const dailyKey = `toondle_daily_game_${formatToInputDate(selectedDate)}`;
      const savedState = localStorage.getItem(dailyKey);

      if (savedState && !forceNewGame) {
        try {
          const parsedState = JSON.parse(savedState);
          let loadedSecretToon = null;
          if (parsedState.secretToonName) {
            loadedSecretToon = dailyEligibleToons.find(toon => toon.name === parsedState.secretToonName);
          } else {
            // If secretToonName is not present, use deterministic selection
            const dailyIndex = getDailyToonIndex(selectedDate, dailyEligibleToons);
            loadedSecretToon = dailyEligibleToons[dailyIndex];
          }
          if (loadedSecretToon) {
            setSecretToon(loadedSecretToon);
            setGuesses(parsedState.guesses);
            setGameWon(parsedState.gameWon);
            setGameOver(parsedState.gameOver);
            setMessage(parsedState.message); // Restore message
          } else {
            console.warn("Saved secret toon not found in filtered list. Starting new game from filtered list.");
            const dailyIndex = getDailyToonIndex(selectedDate, dailyEligibleToons);
            selectedToon = dailyEligibleToons[dailyIndex];
            setSecretToon(selectedToon);
            setGuesses([]);
            setGameWon(false);
            setGameOver(false);
            setMessage('');
          }
        } catch (e) {
          console.error("Failed to parse saved game state:", e);
          // Fallback to new game if parsing fails
          const dailyIndex = getDailyToonIndex(selectedDate, dailyEligibleToons);
          selectedToon = dailyEligibleToons[dailyIndex];
          setSecretToon(selectedToon);
          setGuesses([]);
          setGameWon(false);
          setGameOver(false);
          setMessage('');
        }
      } else {
        const dailyIndex = getDailyToonIndex(selectedDate, dailyEligibleToons);
        selectedToon = dailyEligibleToons[dailyIndex];
        setSecretToon(selectedToon);
        setGuesses([]);
        setGameWon(false);
        setGameOver(false);
        setMessage('');
      }
    } else { // unlimited mode
      const randomIndex = Math.floor(Math.random() * allToons.length);
      selectedToon = allToons[randomIndex];
      setSecretToon(selectedToon);
      setGuesses([]);
      setCurrentGuess('');
      setMessage('');
      setGameOver(false);
      setGameWon(false);
    }
    setShowSuggestions(false); // Hide suggestions on new game
    setFilteredToons([]); // Clear filtered toons
  }, [allToons, gameMode, selectedDate, getDailyToonIndex]);

  // Effect to initialize the game once data is loaded or mode/date changes
  useEffect(() => {
    if (allToons.length > 0) {
      initializeGame();
    }
  }, [allToons, gameMode, selectedDate, initializeGame]); // Re-initialize when allToons, gameMode, or selectedDate changes

  // Effect to save game state to local storage for daily mode
  useEffect(() => {
    if (gameMode === 'daily' && secretToon) {
      const dailyKey = `toondle_daily_game_${formatToInputDate(selectedDate)}`;
      const stateToSave = {
        guesses: guesses,
        gameWon: gameWon,
        gameOver: gameOver,
        message: message, // Save the current message as well
      };
      // Only store secretToonName if the game is over (won or lost)
      if (gameOver) {
        stateToSave.secretToonName = secretToon.name;
      }
      localStorage.setItem(dailyKey, JSON.stringify(stateToSave));
    }
  }, [guesses, gameWon, gameOver, message, secretToon, gameMode, selectedDate]);

  // Effect to load completed and started dates on component mount
  useEffect(() => {
    try {
      const savedCompletedDates = JSON.parse(localStorage.getItem('toondle_completed_dates') || '{}');
      setCompletedDates(savedCompletedDates);
      // Re-evaluate started dates on load, as they are not explicitly saved in a separate list
      const newStartedDates = {};
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1); // Check games from the last month for "started" status

      for (let i = 0; i < 31; i++) { // Check roughly last 30 days
        const dateToCheck = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
        const dateString = formatToInputDate(dateToCheck);
        const dailyKey = `toondle_daily_game_${dateString}`;
        const savedState = localStorage.getItem(dailyKey);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          if (!parsedState.gameOver && parsedState.guesses && parsedState.guesses.length > 0) {
            newStartedDates[dateString] = true;
          }
        }
      }
      setStartedDates(newStartedDates);

    } catch (e) {
      console.error("Failed to load dates from local storage:", e);
      setCompletedDates({});
      setStartedDates({});
    }
  }, []);


  // Effect to filter toons as user types
  useEffect(() => {
    if (currentGuess.trim() === '') {
      setFilteredToons([]);
      setShowSuggestions(false);
      return;
    }

    const lowerCaseGuess = currentGuess.toLowerCase();

    // Start with all toons that match the current guess
    let matches = allToons.filter(toon =>
      toon.name.toLowerCase().includes(lowerCaseGuess)
    );

    // Filter out any Toon whose name is present in the current guesses table
    const guessedNames = new Set(guesses.map(g => g.name.trim().toLowerCase()));
    matches = matches.filter(toon => !guessedNames.has(toon.name.trim().toLowerCase()));

    // In daily mode, filter out toons that don't meet the daily eligibility criteria
    if (gameMode === 'daily') {
      matches = matches.filter(toon => {
        const wiki = toon.wiki_content && typeof toon.wiki_content === 'string' ? toon.wiki_content.toLowerCase() : '';
        const name = toon.name ? toon.name.toLowerCase() : '';
        return (
          (
           wiki.includes('sos') ||
           wiki.includes('sellbot task force') ||
           wiki.includes('bank') ||
           wiki.includes('speedway')) ||
          name.includes('clothing shop') ||
          wiki.length > 1000
        );
      });
    }

    setFilteredToons(matches);
    setShowSuggestions(matches.length > 0);
  }, [currentGuess, allToons, guesses, gameMode]);

  // Helper function to compare traits
  const compareTraits = (guessedToon, secretToon) => {
    const result = {};
    const traits = ['gender', 'species', 'color', 'affiliation'];

    traits.forEach(trait => {
      const guessedValue = guessedToon[trait];
      const secretValue = secretToon[trait];

      // Handle array traits (color, affiliation)
      if (Array.isArray(guessedValue) && Array.isArray(secretValue)) {
        const guessedSet = new Set(guessedValue.map(item => String(item).toLowerCase()));
        const secretSet = new Set(secretValue.map(item => String(item).toLowerCase()));

        const intersection = new Set([...guessedSet].filter(x => secretSet.has(x)));

        if (guessedSet.size === secretSet.size && intersection.size === guessedSet.size) {
          // Full match: same elements, same count
          result[trait] = { value: guessedValue, status: 'match' }; // Keep as array for rendering
        } else if (intersection.size > 0) {
          // Partial match: some common elements
          result[trait] = { value: guessedValue, status: 'partial-match' }; // Keep as array for rendering
        } else {
          // No match
          result[trait] = { value: guessedValue, status: 'no-match' }; // Keep as array for rendering
        }
      } else {
        // Handle single value traits (gender, species)
        if (String(guessedValue).toLowerCase() === String(secretValue).toLowerCase()) {
          result[trait] = { value: guessedValue || null, status: 'match' }; // Use null for N/A
        } else {
          result[trait] = { value: guessedValue || null, status: 'no-match' }; // Use null for N/A
        }
      }
    });

    return result;
  };

  // Core logic to submit a guess, reusable by both form and suggestion click
  const submitGuessLogic = (toonName) => {
    if (gameOver || gameWon) return;

    const guessedToon = allToons.find(toon => toon.name.toLowerCase() === toonName.toLowerCase());

    if (!guessedToon) {
      setMessage('Toon not found. Please select a Toon from the list or enter a valid name.');
      return;
    }

    // Check if the Toon has already been guessed
    if (guesses.some(g => g.name.toLowerCase() === toonName.toLowerCase())) {
        setMessage('You already guessed that Toon!');
        return;
    }

    const comparisonResult = compareTraits(guessedToon, secretToon);
    const newGuesses = [...guesses, { ...guessedToon, comparison: comparisonResult }];
    setGuesses(newGuesses);
    setCurrentGuess(''); // Clear input after guess
    setShowSuggestions(false); // Hide suggestions after guess

    let newGameOver = false;
    let newGameWon = false;
    let newMessage = '';

    // Check for win condition
    if (guessedToon.name.toLowerCase() === secretToon.name.toLowerCase()) {
      newMessage = `Congratulations! You guessed ${secretToon.name} in ${newGuesses.length} guesses!`;
      newGameWon = true;
      newGameOver = true;
    } else if (gameMode === 'daily' && newGuesses.length >= MAX_DAILY_GUESSES) {
      newMessage = `Game Over! You ran out of guesses. The Toon was ${secretToon.name}.`;
      newGameOver = true;
    } else {
      newMessage = `Keep guessing! You have ${gameMode === 'daily' ? (MAX_DAILY_GUESSES - newGuesses.length) + ' guesses left.' : 'unlimited guesses.'}`;
    }

    setMessage(newMessage);
    setGameWon(newGameWon);
    setGameOver(newGameOver);

    // Explicitly update completed/started dates here if the game is now over or has guesses
    const dateString = formatToInputDate(selectedDate);
    const currentCompletedDates = JSON.parse(localStorage.getItem('toondle_completed_dates') || '{}');
    const currentStartedDates = { ...startedDates }; // Use state directly for started dates

    if (newGameOver) {
      if (!currentCompletedDates[dateString]) {
        const updatedCompletedDates = { ...currentCompletedDates, [dateString]: true };
        localStorage.setItem('toondle_completed_dates', JSON.stringify(updatedCompletedDates));
        setCompletedDates(updatedCompletedDates);
      }
      // Remove from started if it was there
      if (currentStartedDates[dateString]) {
        delete currentStartedDates[dateString];
        setStartedDates(currentStartedDates);
      }
    } else if (newGuesses.length > 0 && gameMode === 'daily') {
      // Mark as started if not already completed
      if (!currentCompletedDates[dateString] && !currentStartedDates[dateString]) {
        currentStartedDates[dateString] = true;
        setStartedDates(currentStartedDates);
      }
    }
  };

  // Handle user's guess submission (from button click or Enter key)
  const handleGuess = (e) => {
    e.preventDefault();
    submitGuessLogic(currentGuess.trim());
  };

  // Handle selection from suggestions (now also submits the guess)
  const handleSelectSuggestion = (toonName) => {
    setCurrentGuess(toonName); // Visually update the input
    setShowSuggestions(false); // Hide suggestions
    submitGuessLogic(toonName); // Immediately submit the guess
  };

  // Handle blur to hide suggestions, with a slight delay to allow click events on suggestions to fire
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 100); // Small delay
  };

  // Handler for mouse move on image
  const handleImageMouseMove = (e, url) => {
    setHoveredImageUrl(url);
    setHoveredImagePos({ x: e.clientX, y: e.clientY });
  };
  const handleImageMouseLeave = () => {
    setHoveredImageUrl(null);
  };

  // Styles for trait comparison status
  const getStatusStyles = (status) => {
    switch (status) {
      case 'match':
        return { backgroundColor: '#28a745', color: '#ffffff' }; // Softer Green
      case 'partial-match':
        return { backgroundColor: '#ffc107', color: '#343a40' }; // Softer Orange
      case 'no-match':
        return { backgroundColor: '#dc3545', color: '#ffffff' }; // Softer Red
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'match':
        return <CheckCircle style={{ width: '20px', height: '20px', color: '#d4edda' }} />; // Lighter green icon
      case 'partial-match':
        return <MinusCircle style={{ width: '20px', height: '20px', color: '#fff3cd' }} />; // Lighter orange icon
      case 'no-match':
        return <XCircle style={{ width: '20px', height: '20px', color: '#f8d7da' }} />; // Lighter red icon
      default:
        return null;
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Function to format a Date object into YYYY-MM-DD string for local storage keys
  const formatToInputDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calendar logic
  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDayIndex = date.getDay(); // 0 for Sunday, 1 for Monday

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of day

    // Add leading empty days for calendar alignment
    for (let i = 0; i < firstDayIndex; i++) {
      days.push({ date: null, isCurrentMonth: false, isToday: false, isSelected: false, isCompleted: false, isStarted: false, isFuture: false });
    }

    while (date.getMonth() === month) {
      const dayDate = new Date(date); // Create a new Date object for each day
      dayDate.setHours(0, 0, 0, 0); // Normalize current day to start of day

      const dateString = formatToInputDate(dayDate);
      const isToday = dateString === getTodayDateString();
      const isSelected = dateString === formatToInputDate(selectedDate);
      const isCompleted = !!completedDates[dateString]; // Check if this date is in completedDates
      const isStarted = !!startedDates[dateString] && !isCompleted; // Check if this date is in startedDates and not completed
      const isFuture = dayDate > today; // Check if the date is in the future

      days.push({
        date: dayDate,
        day: dayDate.getDate(),
        isCurrentMonth: true,
        isToday,
        isSelected,
        isCompleted,
        isStarted, // Add isStarted property
        isFuture
      });
      date.setDate(date.getDate() + 1);
    }

    return days;
  };

  const daysInPickerMonth = getDaysInMonth(pickerMonth, pickerYear);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayOfWeekNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Helper to check if a completed date was a loss (not gameWon)
  const isLossDate = (dateString) => {
    if (!completedDates[dateString]) return false;
    const dailyKey = `toondle_daily_game_${dateString}`;
    const savedState = localStorage.getItem(dailyKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        return parsed.gameWon === false;
      } catch {}
    }
    return false;
  };

  const handleMonthChange = (offset) => {
    const newDate = new Date(pickerYear, pickerMonth + offset, 1);
    setPickerMonth(newDate.getMonth());
    setPickerYear(newDate.getFullYear());
  };

  const handleDayClick = (day) => {
    if (day.date && !day.isFuture) { // Only allow clicking if it's a valid date and not in the future
      setSelectedDate(day.date);
      setShowDatePicker(false); // Close picker after selection
      // When a new date is selected, re-initialize the game for that date
      // The useEffect for selectedDate will handle loading the saved game state
    }
  };

  const today = new Date();
  const isCurrentMonthAndYear = pickerMonth === today.getMonth() && pickerYear === today.getFullYear();


  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#1a202c', fontFamily: 'Inter, sans-serif', color: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem' }}>Loading Toon data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#1a202c', fontFamily: 'Inter, sans-serif', color: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#ef4444' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1a202c', backgroundImage: `url(${backgroundImage})`, backgroundRepeat: "no-repeat", backgroundSize: "cover",backgroundPosition: "center center",backgroundAttachment: "fixed", fontFamily: 'Inter, sans-serif', color: '#e2e8f0', padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Full-size image preview on hover */}
      {hoveredImageUrl && (
        <div
          style={{
            position: 'fixed',
            left: hoveredImagePos.x + 20,
            top: hoveredImagePos.y + 20,
            zIndex: 9999,
            pointerEvents: 'none',
            background: 'rgba(30,32,44,0.95)',
            border: '2px solid #facc15',
            borderRadius: '0.5rem',
            padding: '0.5rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            maxWidth: '300px',
            maxHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={hoveredImageUrl}
            alt="Preview"
            style={{ maxWidth: '280px', maxHeight: '280px', objectFit: 'contain', display: 'block' }}
          />
        </div>
      )}
      <div style={{ backgroundColor: '#2d3748', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0,0, 0.1), 0 4px 6px -2px rgba(0, 0,0, 0.05)', padding: '1.5rem', width: '100%', maxWidth: '960px' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '800', textAlign: 'center', color: '#facc15', marginBottom: '1.5rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
          Toondle
        </h1>
        <p style={{ fontSize: '1.125rem', textAlign: 'center', color: '#cbd5e0', marginBottom: '1rem' }}>
          Guess the secret Toon!
          {gameMode === 'daily' ? ` You have ${MAX_DAILY_GUESSES} guesses.` : ' Unlimited guesses.'}
        </p>

        {/* Mode Selection Buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (gameMode === 'daily') {
                setShowDatePicker(!showDatePicker); // Just toggle picker if already in daily mode
              } else {
                setGameMode('daily'); // Switch to daily mode
                const today = new Date();
                setSelectedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate())); // Reset to current local date
                setPickerMonth(today.getMonth());
                setPickerYear(today.getFullYear());
                setShowDatePicker(true); // Show picker when switching to daily
              }
            }}
            style={{
              backgroundColor: gameMode === 'daily' ? '#facc15' : '#4a5568',
              color: gameMode === 'daily' ? '#1a202c' : '#e2e8f0',
              fontWeight: 'bold',
              padding: '0.5rem 1rem',
              borderRadius: '0.75rem',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
              transition: 'transform 0.3s ease-in-out, background-color 0.3s ease-in-out',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Daily Toondle {gameMode === 'daily' && `(${formatDate(selectedDate)})`}
          </button>
          <button
            onClick={() => {
              setGameMode('unlimited');
              setShowDatePicker(false); // Hide date picker if switching to unlimited
            }}
            style={{
              backgroundColor: gameMode === 'unlimited' ? '#facc15' : '#4a5568',
              color: gameMode === 'unlimited' ? '#1a202c' : '#e2e8f0',
              fontWeight: 'bold',
              padding: '0.5rem 1rem',
              borderRadius: '0.75rem',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
              transition: 'transform 0.3s ease-in-out, background-color 0.3s ease-in-out',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Unlimited Toondle
          </button>
        </div>

        {/* Custom Date Picker for Daily Mode */}
        {gameMode === 'daily' && showDatePicker && (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem', backgroundColor: '#3d4858', borderRadius: '0.75rem', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <button
                onClick={() => handleMonthChange(-1)}
                style={{ background: 'none', border: 'none', color: '#facc15', fontSize: '1.5rem', cursor: 'pointer', padding: '0.5rem' }}
              >
                &lt;
              </button>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                {monthNames[pickerMonth]} {pickerYear}
              </h3>
              <button
                onClick={() => handleMonthChange(1)}
                disabled={isCurrentMonthAndYear} // Disable if current month/year
                style={{
                  background: 'none', border: 'none', color: '#facc15', fontSize: '1.5rem', padding: '0.5rem',
                  cursor: isCurrentMonthAndYear ? 'not-allowed' : 'pointer',
                  opacity: isCurrentMonthAndYear ? 0.5 : 1
                }}
              >
                &gt;
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
              {dayOfWeekNames.map(day => (
                <div key={day} style={{ fontWeight: 'bold', color: '#cbd5e0' }}>{day}</div>
              ))}
              {daysInPickerMonth.map((day, index) => (
                <div
                  key={index}
                  onClick={() => handleDayClick(day)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    cursor: day.date && !day.isFuture ? 'pointer' : 'default',
                    // Order of background color priority: completed > started > selected > today > current month default
                    backgroundColor: day.isCompleted
                      ? (isLossDate(day.date ? formatToInputDate(day.date) : '')
                          ? '#ef4444' // Red for completed but not won
                          : '#2f855a') // Green for completed and won
                      : day.isStarted
                        ? '#ed8936' // Orange for started but not finished
                        : day.isSelected
                          ? '#3b82f6' // Blue for selected
                          : day.isToday
                            ? '#63b3ed' // Light blue for today
                            : (day.isCurrentMonth ? '#4a5568' : 'transparent'), // Default for current month, transparent for padding
                    color: day.isCurrentMonth ? '#e2e8f0' : '#718096',
                    fontWeight: day.isToday || day.isSelected ? 'bold' : 'normal',
                    opacity: day.isCurrentMonth && !day.isFuture ? 1 : 0.4, // Reduce opacity for future dates
                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                    transform: day.isSelected ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: day.isSelected ? '0 0 0 2px #facc15' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '30px', // Ensure minimum size for touch targets
                    minHeight: '30px', // Ensure minimum size for touch targets
                  }}
                  onMouseEnter={(e) => {
                    if (day.date && !day.isFuture) e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    if (day.date && !day.isFuture) e.currentTarget.style.transform = day.isSelected ? 'scale(1.05)' : 'scale(1)';
                  }}
                >
                  {day.day}
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Guess Input Section with Custom Autocomplete */}
        <form onSubmit={handleGuess} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem', position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={currentGuess}
            onChange={(e) => setCurrentGuess(e.target.value)}
            onFocus={() => {
              let results = [];
              if (gameMode === 'daily') {
                results = allToons.filter(toon => {
                  const wiki = toon.wiki_content && typeof toon.wiki_content === 'string' ? toon.wiki_content.toLowerCase() : '';
                  const name = toon.name ? toon.name.toLowerCase() : '';
                  return (
                    (
                     wiki.includes('sos') ||
                     wiki.includes('sellbot task force') ||
                     wiki.includes('bank') ||
                     wiki.includes('speedway')) ||
                    name.includes('clothing shop') ||
                    wiki.length > 1000
                  );
                });
              } else {
                results = allToons;
              }
              // Filter out any Toon whose name is present in the current guesses table
              const guessedNames = new Set(guesses.map(g => g.name.trim().toLowerCase()));
              results = results.filter(toon => !guessedNames.has(toon.name.trim().toLowerCase()));
              setFilteredToons(results);
              setShowSuggestions(results.length > 0);
            }}
            onBlur={handleInputBlur}
            placeholder="Enter Toon name..."
            style={{ flexGrow: 1, padding: '0.75rem', border: '2px solid #3b82f6', backgroundColor: '#4a5568', color: '#e2e8f0', borderRadius: '0.75rem', outline: 'none', boxShadow: '0 0 0 2px rgba(250, 204, 21, 0.5)' }}
            disabled={gameOver && gameMode === 'daily'}
            autoComplete="off"
          />
          {showSuggestions && filteredToons.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, backgroundColor: '#4a5568', border: '1px solid #3b82f6', borderRadius: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', marginTop: '0.25rem', maxHeight: '28rem', overflowY: 'auto' }}>
              {filteredToons.map((toon) => (
                <div
                  key={toon.name}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', cursor: 'pointer', backgroundColor: 'transparent', transition: 'background-color 0.3s ease', borderRadius: '0.5rem', margin: '0.25rem' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectSuggestion(toon.name);
                  }}
                >
                  <img
                    src={toon.image}
                    alt={toon.name}
                    style={{ width: '40px', height: '40px', objectFit: 'contain', border: '1px solid #a0aec0', borderRadius: '0' }}
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/100x100/333333/FFFFFF?text=${toon.name.charAt(0)}` }}
                    onMouseMove={(e) => handleImageMouseMove(e, toon.image)}
                    onMouseLeave={handleImageMouseLeave}
                  />
                  <span style={{ fontWeight: '500', color: '#e2e8f0' }}>{toon.name}</span>
                </div>
              ))}
            </div>
          )}
          <button
            type="submit"
            style={{
              backgroundColor: '#facc15',
              color: '#1a202c',
              fontWeight: 'bold',
              padding: '1rem', // Apply 1rem padding
              borderRadius: '0.75rem',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
              transition: 'transform 0.3s ease-in-out, background-color 0.3s ease-in-out',
              cursor: (gameOver && gameMode === 'daily') ? 'not-allowed' : 'pointer',
              opacity: (gameOver && gameMode === 'daily') ? 0.5 : 1,
              alignSelf: 'flex-start',
              transform: 'scale(0.95)' // Default scale 0.95
            }}
            onMouseEnter={(e) => !(gameOver && gameMode === 'daily') && (e.currentTarget.style.transform = 'scale(0.9)')} // Hover scale 0.9
            onMouseLeave={(e) => !(gameOver && gameMode === 'daily') && (e.currentTarget.style.transform = 'scale(0.95)')} // Revert to 0.95
            onClick={(e) => !(gameOver && gameMode === 'daily') && handleGuess(e)}
            disabled={gameOver && gameMode === 'daily'}
          >
            Guess
          </button>
        </form>

        {/* Message Display */}
        {message && (
          <p style={{ textAlign: 'center', fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem', color: gameWon ? '#48bb78' : gameOver ? '#ef4444' : '#63b3ed' }}>
            {message}
          </p>
        )}

        {/* Play Again Button (only for Unlimited Mode) */}
        {(gameMode === 'unlimited' && (gameOver || gameWon)) && (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <button
              onClick={() => initializeGame(true)} // Pass true to force new game
              style={{ backgroundColor: '#3b82f6', color: '#fff', fontWeight: 'bold', padding: '0.75rem 1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', transition: 'transform 0.3s ease-in-out, background-color 0.3s ease-in-out' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Play Again
            </button>
          </div>
        )}

        {/* Display Correct Toon Image and Name when game is over */}
        {(gameOver || gameWon) && secretToon && (
          <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#3d4858', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => setRevealOpen(o => !o)}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#facc15', margin: 'auto' }}>
                The Toon was{' '}
                <a
                  href={`https://toontownrewritten.wiki/${secretToon.name.replace(/ /g, '_')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#63b3ed', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={e => e.stopPropagation()}
                >
                  {secretToon.name}
                </a>
              </h2>
              {/* Chevron icon AFTER the text */}
              <svg
                style={{
                  width: '1.5em',
                  height: '1.5em',
                  marginLeft: '0.25em',
                  verticalAlign: 'middle',
                  transition: 'transform 0.2s',
                  transform: revealOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                }}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9 6l6 6-6 6" stroke="#facc15" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {revealOpen && (
              <div>
                <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '1rem' }}>
                  {/* Left Side: Wiki Content */}
                  <div style={{ flex: '1', minWidth: '300px', maxWidth: '600px', textAlign: 'left' }}>
                    {secretToon.wiki_content && (
                      <div dangerouslySetInnerHTML={{ __html: convertWikiMarkdownToHtml(secretToon.wiki_content) }} style={{ lineHeight: '1.6', fontSize: '0.9rem' }}></div>
                    )}
                  </div>

                  {/* Right Side: Image and NPC Details */}
                  <div style={{ flex: '0 0 auto', textAlign: 'center', padding: '1rem', border: '1px solid #4a5568', borderRadius: '0.75rem', backgroundColor: '#3d4858' }}>
                    <img
                      src={secretToon.image}
                      alt={secretToon.name}
                      style={{ width: '150px', height: '150px', objectFit: 'contain', display: 'block', margin: '0 auto 1rem auto' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/150x150/333333/FFFFFF?text=${secretToon.name.charAt(0)}` }}
                    />
                    {/* Removed duplicate name display here */}
                    <div style={{ textAlign: 'left', borderTop: '1px solid #4a5568', paddingTop: '1rem', display: 'grid', gridTemplateColumns: 'auto minmax(0, 200px)', gap: '0.5rem 1rem', alignItems: 'center' }}>
                      {secretToon.role && secretToon.role !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Role</p>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0' }}>{secretToon.role}</p>
                        </>
                      )}
                      {secretToon.gender && secretToon.gender !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Gender</p>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0' }}>{secretToon.gender}</p>
                        </>
                      )}
                      {secretToon.species && secretToon.species.length > 0 && secretToon.species[0] !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Species</p>
                          <div>
                            {Array.isArray(secretToon.species)
                              ? secretToon.species.map((item, i) => (
                                  <p key={i} style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{item}</p>
                                ))
                              : <p style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{secretToon.species}</p>
                            }
                          </div>
                        </>
                      )}
                      {secretToon.color && secretToon.color.length > 0 && secretToon.color[0] !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Color</p>
                          <div>
                            {Array.isArray(secretToon.color)
                              ? secretToon.color.map((item, i) => (
                                  <p key={i} style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{item}</p>
                                ))
                              : <p style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{secretToon.color}</p>
                            }
                          </div>
                        </>
                      )}
                      {secretToon.affiliation && secretToon.affiliation.length > 0 && secretToon.affiliation[0] !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Affiliation</p>
                          <div>
                            {Array.isArray(secretToon.affiliation)
                              ? secretToon.affiliation.map((item, i) => (
                                  <p key={i} style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{item}</p>
                                ))
                              : <p style={{ fontSize: '.75rem', color: '#cbd5e0', margin: '0' }}>{secretToon.affiliation}</p>
                            }
                          </div>
                        </>
                      )}
                      {secretToon.bldg && secretToon.bldg !== 'N/A' && (
                        <>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0', fontWeight: 'bold' }}>Building</p>
                          <p style={{ fontSize: '.75rem', color: '#cbd5e0' }}>{secretToon.bldg}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guesses Display */}
        {guesses.length > 0 && (
          <div style={{ marginTop: '2rem', overflowX: 'auto' }}>
            <table style={{ minWidth: '100%', backgroundColor: '#4a5568', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', border: '1px solid #4a5568', overflow: 'hidden', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#2d3748', color: '#e2e8f0' }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568', borderRadius: '0.75rem 0 0 0', width: 0 }}>Image</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568' }}>Name</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568' }}>Gender</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568' }}>Species</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568' }}>Color</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid #4a5568', borderRadius: '0 0.75rem 0 0' }}>Affiliation</th>
                </tr>
              </thead>
              <tbody>
                {[...guesses].reverse().map((guess, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #2d3748', backgroundColor: 'transparent', transition: 'background-color 0.3s ease' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d3748'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td style={{ padding: 0, border: '1px solid #4a5568', textAlign: 'center', backgroundColor: 'rgb(45, 55, 72)' }}>
                      <img
                        src={guess.image}
                        alt={guess.name}
                        style={{ width: '75px', objectFit: 'contain', margin: '0 auto', border: 'none', padding: 0, display: 'block' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/100x100/333333/FFFFFF?text=${guess.name.charAt(0)}` }}
                        onMouseMove={(e) => handleImageMouseMove(e, guess.image)}
                        onMouseLeave={handleImageMouseLeave}
                      />
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #4a5568', fontWeight: '500', color: '#e2e8f0', backgroundColor: 'rgb(45, 55, 72)' }}>
                      {guess.name}
                    </td>
                    {['gender', 'species', 'color', 'affiliation'].map(trait => (
                      <td key={trait} style={{ padding: '1rem', border: '1px solid #4a5568', ...getStatusStyles(guess.comparison[trait]?.status) }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {getStatusIcon(guess.comparison[trait]?.status)}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            {Array.isArray(guess.comparison[trait]?.value)
                              ? guess.comparison[trait].value.map((item, i) => (
                                  <span key={i} style={{ display: 'block', whiteSpace: 'nowrap' }}>{item}</span>
                                ))
                              : <span>{guess.comparison[trait]?.value || 'N/A'}</span>
                            }
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
